import { DndContext, DragEndEvent, KeyboardSensor, MeasuringStrategy, MouseSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arraySwap, rectSwappingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Button, Grid, IconButton, Tooltip, Typography, Alert as MuiAlert, AlertTitle } from '@mui/material';
import { useEffect, useState } from 'react';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CustomLoader } from '../../../../shared/lib/common/CustomLoader';
import { PageHeading } from '../../../../shared/lib/common/PageHeading';
import {
  CUSTOMER_SETTINGS_TAB_CONTACT_LIST,
  GRAPHQL_QUERY_POLICY,
  LABEL_CONTACTS_OPTED_OUT,
  PAGE_TITLE_WELCOME,
  LABEL_SUBSCRIPTION_EXPIRED,
  TOOLTIP_DRAG_AND_DROP_DISABLED,
  TOOLTIP_DRAG_AND_DROP_ENABLED,
  BUTTON_TEXT_ADD_NEW_CONTACT,
  TOOLTIP_CONTACT_LIMIT_REACHED,
  LABEL_STANDARD_PLAN,
  NOTE_DRAG_CONTACTS_REORDER,
  LABEL_PHONE_REVERIFY_REQUIRED,
} from '../../../../shared/lib/constants';
import { LINKS, MSG_MAX_CONTACTS, MSG_PRIORITY_UPDATED, TOOLTIP_MAX_CONTACTS_INFO } from '../../app/constants';
import {
  Contact,
  Plan,
  useFetchAllContactsLazyQuery,
  useFetchAllOptedOutContactsLazyQuery,
  useGetSubscriptionDetailLazyQuery,
  useSwapContactPriorityMutation
} from '../../generated';
import palette from '../../../../shared/lib/theme/palette';
import { AddContactWrapper, flexCenter, lockStyle, reverifyAlertButtonMobileStyle, reverifyAlertMobileStyle, reverifyAlertStyle } from '../../../../shared/lib/theme/styleConstants';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { AddContact } from './AddContact';
import { DashboardCard } from './DashboardCard';
import { DeleteContact } from './DeleteContact';
import { EditContact } from './EditContact';
import { useAppContext } from '../../app/contexts/AppContext';
import { ChangeContactPriority } from './ChangeContactPriority';
import { Link, useNavigate } from 'react-router-dom';
import { RingMyPhoneButton } from '../ringMyPhone/RingMyPhoneButton';
import { EditContactNumber } from '../accountSettings/EditContactNumber';
import { useMobileView } from '../../app/hooks/useMobileView';

const measuringConfig = {
  droppable: {
    strategy: MeasuringStrategy.Always
  }
};

export const DashBoardCards = () => {
  const { currentUser, isOpen, dispatch, cardsData, optOutContact, currentCardData, contactCount, selectedPlan, dragAndDrop } = useAppContext();
  const { isSubscribed, email, phoneVerified, phoneNumberSid, phoneNumber } = currentUser ?? {};
  const [hideBackDrop, setHideBackDrop] = useState(false);
  const { customerStripeId, subscriptionCancelled } = isSubscribed ?? {};
  const { blackTwo } = palette;
  const { metadata } = selectedPlan ?? {};
  const { contacts } = metadata ?? {};
  const mediumViewPort = useMobileView();
  const updatedContactsValue = parseInt(contacts);
  const [subscriptionPlan, setSubscriptionPlan] = useState<Plan | null>(null);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const [getSubscriptionDetail] = useGetSubscriptionDetailLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { getSubscriptionDetail } = data;

      if (getSubscriptionDetail) {
        const { data: getSubscriptionDetailData } = getSubscriptionDetail ?? {};
        const { items } = getSubscriptionDetailData[0] ?? {};
        const { data: itemsData } = items ?? {};
        const { plan } = itemsData.slice(-1)[0] ?? {};
        setSubscriptionPlan(plan);
        const { metadata } = plan;
        const { contacts } = metadata;
        const contactsValue = parseInt(contacts);
        dispatch({ type: ActionType.SET_CONTACT_COUNT, contactCount: contactsValue });
      }
    }
  });

  useEffect(() => {
    if (customerStripeId) {
      (async () =>
        await getSubscriptionDetail({
          variables: {
            customerStripeId
          }
        }))();
    }
  }, [customerStripeId, email]);

  const [getContacts, { loading: contactLoading, refetch }] = useFetchAllContactsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchAllContacts } = data ?? {};

      if (fetchAllContacts) {
        dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: fetchAllContacts });
      }
    }
  });

  const [getOptOutContacts, { loading: optOutLoading }] = useFetchAllOptedOutContactsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchAllOptedOutContacts } = data ?? {};

      if (fetchAllOptedOutContacts) {
        dispatch({ type: ActionType.SET_CONTACTS_OPT_OUT, optOutContact: fetchAllOptedOutContacts });
      }
    }
  });

  useEffect(() => {
    (async () => await getContacts())();

    return () => {
      !customerStripeId && dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: [] });
    };
  }, [customerStripeId, email]);

  useEffect(() => {
    (async () => await getOptOutContacts())();

    return () => {
      !customerStripeId && dispatch({ type: ActionType.SET_CONTACTS_OPT_OUT, optOutContact: [] });
    };
  }, [customerStripeId, email]);

  const [swapContactPriority, { loading: swapLoading }] = useSwapContactPriorityMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { swapContactPriority } = data || {};

      if (swapContactPriority) {
        Alert.success(MSG_PRIORITY_UPDATED);
        dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: swapContactPriority });
      }
    }
  });

  const addContactOpen = () => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen });
  };

  const onDragStart = () => {
    dispatch({ type: ActionType.SET_IS_DRAGGING, isDragging: true });
  };

  const onDragCancel = () => {
    dispatch({ type: ActionType.SET_IS_DRAGGING, isDragging: false });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const activeContact = cardsData.filter((card) => card.id === active.id);
    const overContact = cardsData.filter((card) => card.id === over?.id);

    const { priority: activeCardPriority } = activeContact[0] || {};
    const { priority: overContactPriority } = overContact[0] || {};

    const dragAndOver = active && over?.id;

    if (dragAndOver) {
      const oldIndex = cardsData.findIndex((item) => item.id === active.id);
      const newIndex = cardsData?.findIndex((item) => item.id === over.id);
      dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: arraySwap(cardsData, oldIndex, newIndex) });

      activeContact[0].id !== overContact[0].id &&
        overContactPriority !== activeCardPriority &&
        (await swapContactPriority({
          variables: {
            swapContactPriorityInput: {
              idOne: activeContact[0].id,
              idTwo: overContact[0].id,
              priorityOne: activeCardPriority,
              priorityTwo: overContactPriority
            }
          }
        }));
    }
    dispatch({ type: ActionType.SET_IS_DRAGGING, isDragging: false });
  };

  const handleVip = async (contact1: Contact, contact2: Contact) => {
    await swapContactPriority({
      variables: {
        swapContactPriorityInput: {
          idOne: contact1.id,
          idTwo: contact2.id,
          priorityOne: contact1.priority,
          priorityTwo: contact2.priority
        }
      }
    });
  };

  const cardsValueUpdated = isNaN(updatedContactsValue) ? contactCount : updatedContactsValue;

  const cardsLength = cardsData.length >= cardsValueUpdated;

  const loading = contactLoading || optOutLoading || swapLoading;
  return (
    <Box>
      {subscriptionCancelled && (
        <Box p={1} mb={2}>
          <MuiAlert severity="error">
            <AlertTitle>{LABEL_SUBSCRIPTION_EXPIRED}</AlertTitle>
            Please subscribe to a plan to reactivate access. All features are currently disabled until a valid plan is selected.
            <Box component={Link} to={LINKS.CHANGE_PLAN_LINK}>
              {'   '}
              Click here{' '}
            </Box>{' '}
            to purchase a subscription plan
          </MuiAlert>
        </Box>
      )}

      {phoneVerified && !phoneNumberSid && (
        <Box p={1} mb={2}>
          <MuiAlert
            severity="warning"
            sx={mediumViewPort ? reverifyAlertMobileStyle : reverifyAlertStyle}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                sx={mediumViewPort ? reverifyAlertButtonMobileStyle : undefined}
                onClick={() => dispatch({ type: ActionType.SET_IS_PHONE_EDIT, isPhoneEdit: true })}
              >
                Verify Now
              </Button>
            }
          >
            <AlertTitle>{LABEL_PHONE_REVERIFY_REQUIRED}</AlertTitle>
            {'An OTP verification call is needed to ensure your caller ID ('}
            <strong>...{phoneNumber?.slice(-4) ?? ''}</strong>
            {') displays correctly when calling your contacts from a borrowed phone.'}
          </MuiAlert>
        </Box>
      )}

      <EditContactNumber hideBackDrop={hideBackDrop} setHideBackDrop={setHideBackDrop} isReverifyMode />

      <Grid container>
        <Grid item xs={12} sm={6} md={9}>
          <Box display="flex" alignItems="flex-end" justifyContent="normal">
            <Box>
              <PageHeading
                color="blackOne"
                title={cardsData.length ? CUSTOMER_SETTINGS_TAB_CONTACT_LIST : PAGE_TITLE_WELCOME}
                subTitle={
                  cardsData.length
                    ? mediumViewPort
                      ? ''
                      : cardsData.length > 1
                        ? NOTE_DRAG_CONTACTS_REORDER
                        : ''
                    : 'You don’t have any Trusted Contact in your list. Click on button below to start adding contacts.'
                }
              />
            </Box>
            <Box position="relative">
              <Box sx={lockStyle}>
                {cardsData.length > 1 && !mediumViewPort && (
                  <Tooltip title={!dragAndDrop ? TOOLTIP_DRAG_AND_DROP_DISABLED : TOOLTIP_DRAG_AND_DROP_ENABLED}>
                    <IconButton color="primary" onClick={() => dispatch({ type: ActionType.SET_DRAG_AND_DROP, dragAndDrop: !dragAndDrop })}>
                      {!dragAndDrop ? <LockIcon /> : <LockOpenIcon />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box mb={3} display="flex" justifyContent="flex-end">
            <RingMyPhoneButton loading={loading} />
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <CustomLoader height="calc(100vh - 366px)" />
      ) : (
        <Box>
          <Grid container spacing={2} rowSpacing={2}>
            <DndContext measuring={measuringConfig} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragStart={onDragStart} onDragCancel={onDragCancel}>
              <SortableContext items={cardsData} strategy={rectSwappingStrategy}>
                {cardsData?.map((contact, index) => {
                  const { id } = contact ?? {};

                  return (
                    <Grid item lg={4} xs={12} key={id}>
                      <DashboardCard index={index} contact={contact} handleVip={handleVip} planName={subscriptionPlan?.nickname || ''} />
                    </Grid>
                  );
                })}
              </SortableContext>
            </DndContext>

            {!cardsLength && (
              <Grid item lg={4} xs={12}>
                <Box sx={AddContactWrapper}>
                  <Box height="178px" sx={{ ...flexCenter, flexDirection: 'column' }}>
                    <Button onClick={addContactOpen} startIcon={<AddIcon />} variant="outlined" color="primary" disabled={subscriptionCancelled ?? false} sx={{ marginBottom: '16px' }}>
                      {BUTTON_TEXT_ADD_NEW_CONTACT}
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Typography sx={{ color: blackTwo }}>{MSG_MAX_CONTACTS(cardsValueUpdated)}</Typography>
                      <Tooltip title={TOOLTIP_MAX_CONTACTS_INFO} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: '1rem', color: blackTwo, cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            {cardsLength && subscriptionPlan?.nickname === 'Free plan' && (
              <Grid item lg={4} xs={12}>
                <Box sx={AddContactWrapper} position="relative">
                  <Box
                    height="178px"
                    onClick={() => {
                      navigate(LINKS.CHANGE_PLAN_LINK);
                    }}
                    sx={{ ...flexCenter, flexDirection: 'column' }}
                  >
                    <Tooltip title={TOOLTIP_CONTACT_LIMIT_REACHED} placement="top" arrow>
                      <Box sx={{ cursor: 'pointer' }}>
                        <Button startIcon={<AddIcon />} variant="outlined" color="primary" disabled sx={{ marginBottom: '16px' }}>
                          {BUTTON_TEXT_ADD_NEW_CONTACT}
                        </Button>
                      </Box>
                    </Tooltip>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Typography sx={{ color: blackTwo }}>{MSG_MAX_CONTACTS(cardsValueUpdated)}</Typography>
                      <Tooltip title={TOOLTIP_MAX_CONTACTS_INFO} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: '1rem', color: blackTwo, cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* 👇 Plan name centered at the bottom */}
                  <Box
                    position="absolute"
                    bottom="10px"
                    left="50%"
                    sx={{
                      transform: 'translateX(-50%)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: blackTwo, fontWeight: '600' }}>
                      {LABEL_STANDARD_PLAN}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          <AddContact refetch={refetch} />

          <EditContact currentCardData={currentCardData} refetch={refetch} />

          <DeleteContact currentCardData={currentCardData} refetch={refetch} />

          <ChangeContactPriority currentCardData={currentCardData} refetch={refetch} />
        </Box>
      )}

      {optOutContact?.length > 0 && (
        <>
          <Box display="flex" alignItems="flex-end" justifyContent="normal" mt={5} mb={3}>
            <Box>
              <PageHeading color="blackOne" title={LABEL_CONTACTS_OPTED_OUT} />
            </Box>
          </Box>

          {loading ? (
            <CustomLoader height="calc(100vh - 366px)" />
          ) : (
            <Box mb={2}>
              <Grid container spacing={2} rowSpacing={2}>
                {optOutContact?.map((contact, index) => {
                  const { id } = contact ?? {};

                  return (
                    <Grid item lg={4} xs={12} key={id}>
                      <DashboardCard index={index} contact={contact} declined={true} />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

    </Box>
  );
};
